import { useEffect, useState } from 'react';
import { supabase } from './supabase';

// Runtime-configurable settings live in the project's own Supabase `global_settings`
// table (managed from the SynthOS Admin panel). We read live values and fall back to
// these defaults (which mirror .synth/admin/schema.json) when a key is unset.

export type SettingsShape = {
  announcement_enabled: boolean;
  announcement_text: string;
  hero_headline: string;
  hero_subhead: string;
  primary_cta_label: string;
  response_window_hours: number;
  phone_support_note: string;
  stat_response_hours: number;
  stat_contractors: number;
  stat_metros: number;
  coverage_all_states: boolean;
};

export const SETTINGS_DEFAULTS: SettingsShape = {
  announcement_enabled: true,
  announcement_text: "Rosa is matching families with CAPS-certified specialists across the country.",
  hero_headline: "Safe at home, before she's home.",
  hero_subhead:
    "After a fall or a hospital stay, you need one trusted local specialist, not a dozen sales calls. Rosa matches your family with a single CAPS-certified expert and gets a free in-home safety assessment on the calendar, usually this week.",
  primary_cta_label: 'Get a free safety assessment',
  response_window_hours: 48,
  phone_support_note: '',
  stat_response_hours: 48,
  stat_contractors: 2000,
  stat_metros: 25,
  coverage_all_states: true,
};

let cache: Partial<SettingsShape> | null = null;

export async function loadSettings(): Promise<Partial<SettingsShape>> {
  if (cache) return cache;
  if (!supabase) return (cache = {});
  try {
    const { data, error } = await supabase.from('global_settings').select('key, value');
    if (error || !data) return (cache = {});
    const out: Record<string, unknown> = {};
    for (const row of data as { key: string; value: unknown }[]) out[row.key] = row.value;
    return (cache = out as Partial<SettingsShape>);
  } catch {
    return (cache = {});
  }
}

export function useSettings(): SettingsShape {
  const [settings, setSettings] = useState<SettingsShape>(SETTINGS_DEFAULTS);
  useEffect(() => {
    let live = true;
    loadSettings().then((live_) => {
      if (live) setSettings({ ...SETTINGS_DEFAULTS, ...live_ });
    });
    return () => {
      live = false;
    };
  }, []);
  return settings;
}
